import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild } from "@angular/core";
import { Router } from '@angular/router';
import { ApiNotification, ApiUser, NotificationsApi } from '@tytapp/api';
import { Shell, SiteWideAlert } from '@tytapp/common';
import { UserService } from "@tytapp/user";
import { Subscription } from "rxjs";
import { debounceTime } from 'rxjs/operators';
import { NotificationItem, NotificationsService } from "../notifications.service";

@Component({
    selector: 'tyt-notifications-list',
    templateUrl: './notifications-list.component.html',
    styleUrls: ['./notifications-list.component.scss']
})
export class NotificationsListComponent implements OnInit, OnDestroy {

    private subs: Subscription = new Subscription();
    user!: ApiUser;

    showUnseenMarkers = true;

    currentPage = 1;

    wideAlerts: SiteWideAlert[];

    constructor(
        private notifications: NotificationsService,
        private userService: UserService,
        private router: Router,
        private notificationApi: NotificationsApi,
        private renderer: Renderer2,
        private shell: Shell
    ) {
        this.watchTrayOpen();
        this.watchListScroll();
    }

    visible: boolean = false;
    hidden: boolean = true;
    touchable: boolean = false;

    handleKeys(event: KeyboardEvent) {
        if (!this.visible)
            return;

        if (event.code === 'Escape') {
            this.notifications.visible = false;
            event.preventDefault();
            event.stopPropagation();
        }
    }

    close() {
        this.notifications.closeList();
    }

    list: NotificationItem[] = [];
    extraMenuNotification: NotificationItem;

    finishTransition;

    showDrawer() {
        this.hidden = false;
        this.touchable = false;
        clearTimeout(this.finishTransition);
        setTimeout(() => this.visible = true);
        this.finishTransition = setTimeout(() => {
            this.touchable = true;
            if (this.userCard) {
                this.userCard.nativeElement.focus();
            }
        }, 400);
    }

    @ViewChild('userCard') userCard: ElementRef<HTMLElement>;

    hideDrawer() {
        this.visible = false;
        this.touchable = false;
        clearTimeout(this.finishTransition);
        this.finishTransition = setTimeout(() => {
            this.hidden = true;
        }, 400);
    }

    ngOnInit() {
        this.subs.add(this.router.events.subscribe(() => this.notifications.visible = false));
        this.subs.add(this.notifications.changed.subscribe(ns => this.list = ns));
        this.subs.add(this.notifications.visibleChange.subscribe(visible => {
            if (this.visible !== visible) {
                if (visible) {
                    this.showDrawer();
                } else {
                    this.hideDrawer();
                }
            }
        }));
        this.subs.add(this.userService.userChanged.subscribe(user => {
            if (!user) this.notifications.visible = false;
            this.user = user;
        }));

        this.shell.alertsChanged.subscribe(alerts => this.wideAlerts = alerts);
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }

    dismiss(notification: NotificationItem) {
        this.notifications.dismiss(notification);
    }

    async watchTrayOpen() {
        if (typeof window === 'undefined')
            return;

        if (!await this.shell.hasFeature('apps.web.enable_server_notifications'))
            return;

        this.subs.add(
            this.notifications.visibleChange.subscribe(visible => {
                if (visible) {

                    const lastItem = this.notifications.list[0];
                    if (!lastItem.demoOnly) {
                        this.notificationApi.patchNotification(lastItem.id, {
                            read: true
                        }).toPromise().then(() => {
                            this.notifications.markSeen();
                        });
                    }
                    this.showUnseenMarkers = false;
                }
            })
        );
    }

    public overlayClicked(event: any): void {
        if (event.target.className.indexOf('notifications-overlay') >= 0) {
            this.notifications.visible = false;
        }
    }

    get publicProfile() {
        if (!this.user.username) return null;
        return `/community/profiles/${this.user.username}`;
    }

    isUrlExternal(url: string) {
        return url && url.startsWith('https:');
    }

    loading = false;
    async loadMore() {
        if (!await this.shell.hasFeature('apps.web.enable_server_notifications'))
            return;
        this.currentPage + 1;
        this.loading = true;
        const notificationList: ApiNotification[] = await this.notificationApi.getNotifications(this.currentPage).toPromise();

        notificationList.forEach(n => {
            const notification: NotificationItem = {
                id: n.id,
                category: <any>n.category,
                text: n.text,
                url: n.url,
                open_in_new_tab: n.open_in_new_tab,
                description: n.text,
                timestamp: new Date(n.date),
                icon: n.icon,
                extra_actions: n.extra_actions,
                style: n.style as any,
                image: n.image
            }
            this.notifications.add(notification, false)
        });
        this.loading = false;
    }

    get hasMoreItems() {
        return this.notifications.hasMoreItems;
    }

    @ViewChild('notificationListBox') notificationBox: ElementRef;
    async watchListScroll() {
        if (typeof window === 'undefined')
            return;

        if (!await this.shell.hasFeature('apps.web.enable_server_notifications'))
            return;

        this.notifications.visibleChange
        .subscribe((visible) => {
            if (visible && this.notificationBox && this.hasMoreItems) {
                this.notificationBox.nativeElement.onscroll = (event) => {
                    const listElement = event.target;
                    if (listElement.scrollTop + listElement.clientHeight >= listElement.scrollHeight) {
                        this.loadMore();
                    }
                }
            }
        })
    }

}
